import { useState, useEffect } from "react";
import { Header } from "@/components/Header";
import { TipCard } from "@/components/TipCard";
import { SEOHead } from "@/components/SEOHead";
import { StructuredData } from "@/components/StructuredData";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Plus, Zap } from "lucide-react";
import { useNavigate } from "react-router-dom";

const Tips = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tips, setTips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSport, setSelectedSport] = useState("all");
  const [filterType, setFilterType] = useState("all");

  const sports = ["Football", "Basketball", "Tennis", "Baseball", "Hockey", "Cricket"];

  useEffect(() => {
    fetchTips();
  }, [selectedSport]);

  const fetchTips = async () => {
    try {
      setLoading(true);
      
      // Query only active, valid tips
      const { data, error } = await supabase
        .from('tips')
        .select(`
          id,
          sport,
          league,
          match_info,
          tip_type,
          odds,
          confidence,
          price,
          currency,
          is_premium,
          is_ai_generated,
          expires_at,
          tipsters (
            profiles (
              display_name
            )
          )
        `)
        .eq('status', 'pending')
        .gte('expires_at', new Date().toISOString())
        .order('expires_at', { ascending: true })
        .limit(50);

      if (error) throw error;
      
      // Transform data to match TipCard expectations
      const transformedTips = data?.map((t: any) => ({
        id: t.id,
        sport: t.sport,
        league: t.league,
        match: t.match_info,
        tipType: t.tip_type,
        odds: t.odds,
        confidence: t.confidence,
        price: t.price,
        currency: t.currency,
        isPremium: t.is_premium,
        isAI: t.is_ai_generated,
        expiresAt: t.expires_at,
        timeLeft: calculateTimeLeft(t.expires_at),
        tipsterName: t.tipsters?.profiles?.display_name || 'Anonymous'
      })) || [];

      setTips(transformedTips);
    } catch (error) {
      console.error("Error fetching tips:", error);
    } finally {
      setLoading(false);
    }
  };

  const calculateTimeLeft = (expiresAt: string | null) => {
    if (!expiresAt) return "No expiry";
    
    const now = new Date();
    const expiry = new Date(expiresAt);
    const diff = expiry.getTime() - now.getTime();
    
    if (diff <= 0) {
      return `Expired on ${expiry.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })}`;
    }
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    return `${hours}h ${minutes}m`;
  };

  const filteredTips = tips.filter((tip: any) => {
    if (filterType === "free") return tip.price === 0;
    if (filterType === "premium") return tip.price > 0;
    if (filterType === "ai") return tip.isAI;
    return true;
  });

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="BetVerse Tips – Live Sports Betting Predictions & Analysis"
        description="Browse active betting tips from verified tipsters and AI. Find winning picks for football, basketball, tennis and more."
        canonical="https://betverse-global-picks.lovable.app/tips"
      />
      
      {/* Structured data for tips list */}
      <StructuredData
        type="ItemList"
        data={{
          title: "Latest Betting Tips",
          items: filteredTips.slice(0, 10).map((tip: any) => ({
            name: tip.match,
            id: tip.id
          }))
        }}
      />
      
      {/* SportsEvent schema for first 5 tips */}
      {filteredTips.slice(0, 5).map((tip: any) => (
        <StructuredData
          key={tip.id}
          type="SportsEvent"
          data={{
            name: tip.match,
            sport: tip.sport,
            league: tip.league,
            startDate: tip.expiresAt,
            price: tip.price,
            currency: tip.currency || 'USD'
          }}
        />
      ))}
      
      <Header />
      
      <div className="container mx-auto px-4 py-8">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-2">Latest Tips & Predictions</h1>
            <p className="text-muted-foreground">Fresh insights from verified tipsters and AI models</p>
          </div>
          
          {user && (
            <Button onClick={() => navigate('/create-tip')} className="flex items-center gap-2">
              <Plus className="h-4 w-4" />
              Create Tip
            </Button>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <Select value={selectedSport} onValueChange={setSelectedSport}>
            <SelectTrigger className="w-full md:w-48">
              <SelectValue placeholder="All Sports" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sports</SelectItem>
              {sports.map(sport => (
                <SelectItem key={sport} value={sport}>{sport}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <div className="flex gap-2">
            <Button
              variant={filterType === "all" ? "default" : "outline"}
              onClick={() => setFilterType("all")}
              size="sm"
            >
              All Tips
            </Button>
            <Button
              variant={filterType === "free" ? "default" : "outline"}
              onClick={() => setFilterType("free")}
              size="sm"
            >
              Free
            </Button>
            <Button
              variant={filterType === "premium" ? "default" : "outline"}
              onClick={() => setFilterType("premium")}
              size="sm"
            >
              Premium
            </Button>
            <Button
              variant={filterType === "ai" ? "default" : "outline"}
              onClick={() => setFilterType("ai")}
              size="sm"
            >
              <Zap className="h-3 w-3 mr-1" />
              AI
            </Button>
          </div>
        </div>

        {/* Results Count */}
        <div className="flex items-center gap-2 mb-6">
          <Badge variant="outline">{filteredTips.length} tips available</Badge>
          {selectedSport && <Badge variant="secondary">{selectedSport}</Badge>}
          {filterType !== "all" && <Badge variant="secondary">{filterType}</Badge>}
        </div>

        {/* Tips Grid */}
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="bg-card rounded-lg h-80"></div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredTips.map((tip: any) => (
              <TipCard key={tip.id} tip={tip} />
            ))}
          </div>
        )}

        {filteredTips.length === 0 && !loading && (
          <div className="text-center py-12">
            <div className="text-muted-foreground mb-4">No tips found matching your criteria</div>
            <Button variant="outline" onClick={() => {
              setSelectedSport("all");
              setFilterType("all");
            }}>
              Clear Filters
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Tips;